/**
 * Botão fixo de feedback — FAB no canto inferior esquerdo.
 * Visível em todas as views. Abre dialog para envio de sugestão/bug/dúvida.
 * Ocultado quando feedbackEnabled === false na config do servidor.
 */

import React, { useState, useCallback } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { useUIConfigContext } from '../../contexts/UIConfigContext'
import { useNPS } from '../../contexts/NPSContext'
import { submitFeedback } from '../../services/api'

const FEEDBACK_TYPES = [
  { value: 'SUGGESTION', label: 'Sugestão de melhoria' },
  { value: 'BUG', label: 'Reportar problema' },
  { value: 'QUESTION', label: 'Dúvida' },
  { value: 'OTHER', label: 'Outro' }
]

export default function FeedbackButton() {
  const { uiConfig } = useUIConfigContext()
  const { currentView } = useNPS()

  const [open, setOpen] = useState(false)
  const [type, setType] = useState('SUGGESTION')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Respeita flag feedbackEnabled da config do servidor
  if (uiConfig?.npsConfig?.feedbackEnabled === false) {
    return null
  }

  const handleClose = useCallback(() => {
    setOpen(false)
    setMessage('')
    setType('SUGGESTION')
    setSubmitted(false)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return
    setSubmitting(true)
    try {
      await submitFeedback({ type, message: message.trim(), currentView })
      setSubmitted(true)
      setTimeout(handleClose, 2000)
    } catch {
      // Silencia erro — não bloquear o usuário por falha de feedback
    } finally {
      setSubmitting(false)
    }
  }, [type, message, currentView, handleClose])

  const fabSx = {
    position: 'fixed' as const,
    bottom: 24,
    left: 24,
    zIndex: 1200,
    backgroundColor: '#ffffff !important',
    color: '#666666 !important',
    border: '1px solid rgba(0,0,0,0.12) !important',
    boxShadow: '0 3px 5px -1px rgba(0,0,0,0.1), 0 6px 10px 0 rgba(0,0,0,0.07), 0 1px 18px 0 rgba(0,0,0,0.06) !important',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
    textTransform: 'none !important' as any,
    lineHeight: '1.75 !important',
    minHeight: '34px !important',
    height: '34px !important',
    backgroundImage: 'none !important',
    '&:hover': {
      backgroundColor: '#f5f5f5 !important',
      boxShadow: '0 5px 5px -3px rgba(0,0,0,0.1), 0 8px 10px 1px rgba(0,0,0,0.07), 0 3px 14px 2px rgba(0,0,0,0.06) !important',
      color: '#1976d2 !important',
      borderColor: '#90caf9 !important'
    }
  }

  return (
    <>
      {/* Desktop: FAB estendido com texto */}
      <Tooltip title="Enviar sugestão ou reportar problema" placement="right">
        <Fab
          variant="extended"
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            ...fabSx,
            px: 1.5,
            gap: 0.75,
            display: { xs: 'none', md: 'flex' }
          }}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: '16px !important', width: '16px !important', height: '16px !important', fill: 'currentColor !important' }} />
          <Box component="span" sx={{ fontSize: '0.75rem !important', fontWeight: '500 !important', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important', textTransform: 'uppercase !important' as any, letterSpacing: '0.02857em !important', color: 'inherit !important' }}>
            Sugestões
          </Box>
        </Fab>
      </Tooltip>

      {/* Mobile: FAB ícone apenas */}
      <Tooltip title="Enviar sugestão" placement="right">
        <Fab
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            ...fabSx,
            display: { xs: 'flex', md: 'none' }
          }}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: '20px !important', width: '20px !important', height: '20px !important', fill: 'currentColor !important' }} />
        </Fab>
      </Tooltip>

      {/* Dialog de feedback */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {submitted ? 'Feedback enviado!' : 'Enviar sugestão'}
        </DialogTitle>

        {!submitted ? (
          <>
            <DialogContent sx={{ pt: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sua opinião nos ajuda a melhorar a ferramenta.
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={type}
                  label="Tipo"
                  onChange={e => setType(e.target.value)}
                >
                  {FEEDBACK_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                multiline
                rows={4}
                fullWidth
                label="Mensagem"
                placeholder="Descreva sua sugestão, problema ou dúvida..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                size="small"
              />
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleClose} color="inherit" sx={{ textTransform: 'none' }}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
                sx={{ textTransform: 'none' }}
              >
                Enviar
              </Button>
            </DialogActions>
          </>
        ) : (
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Obrigado pelo seu feedback! Vamos analisar sua mensagem em breve.
            </Typography>
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}
